import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { Client } from "../../domain/client/client.js";
import { ClientStatus } from "../../domain/client/client.js";
import { ProfessionalStatus, type Professional } from "../../domain/identity/professional.js";
import { isValidCnpj, maskCnpj, formatCnpj } from "../../domain/identity/company.js";
import { PermissionCode } from "../../permissions/permissions.js";

export default class AdminClientsPage extends HTMLElement {
  private clients: Client[] = [];
  private searchQuery = "";
  private statusFilter = "ALL";
  private isModalOpen = false;
  /** Resultado da última substituição de PLH, mostrado na listagem. */
  private replacementNotice = "";
  private editingClient: Client | null = null;
  private deleteModal = { isOpen: false, id: "", title: "", message: "", error: "" };
  private boundHandleMasterDataUpdated: (e: Event) => void;
  /** Candidatos a PLH responsável do Cliente em edição (gate oficial). */
  private plhCandidates: Array<Professional & { missingForApproval: string[] }> = [];

  /**
   * SUBSTITUIÇÃO DO PLH — trocar quem responde por um Cliente não é uma
   * edição de campo qualquer: decide para onde vão as próximas aprovações e
   * o que acontece com as que já estão na fila. Por isso passa por uma
   * confirmação própria, com a contagem real de pendentes.
   */
  private plhReplacement: {
    newPlhProfessionalId: string;
    currentPlhName: string;
    nextPlhName: string;
    pendingCount: number;
    reassignPending: boolean;
    busy: boolean;
    error: string;
    pendingSave: { name: string; legalName: string; document: string } | null;
  } | null = null;
  /** Origem do deep link "CONFIGURAR PLH DO CLIENTE" — volta ao documento depois de salvar. */
  private returnTo: string | null = null;
  private focusPlh = false;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  public connectedCallback(): void {
    this.readDeepLink();
    this.loadData();
    window.addEventListener("active-client-changed", () => this.loadData());
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  /**
   * Deep link vindo de um documento bloqueado por falta de PLH
   * (?clientId=…&focus=plh&returnTo=…): abre o Cliente certo já no cadastro,
   * com o campo PLH RESPONSÁVEL em destaque, e guarda a origem para voltar.
   */
  private readDeepLink(): void {
    const params = new URLSearchParams(window.location.search);
    const clientId = params.get("clientId");
    this.focusPlh = params.get("focus") === "plh";
    this.returnTo = params.get("returnTo");
    if (clientId) this.pendingClientId = clientId;
  }

  private pendingClientId: string | null = null;

  public disconnectedCallback(): void {
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      this.clients = await platform.adminService.listClients(dummyContext);
      await this.loadResponsiblePlhNames(dummyContext);
      if (this.pendingClientId) {
        const target = this.clients.find(c => String(c.id) === this.pendingClientId) || null;
        this.pendingClientId = null;
        if (target) {
          await this.openEditor(target);
          return;
        }
      }
      this.render();
    } catch (e) {
      console.error("Failed to load clients", e);
    }
  }

  private plhNames = new Map<string, string>();

  private async loadResponsiblePlhNames(context: any): Promise<void> {
    this.plhNames.clear();
    await Promise.all(this.clients
      .filter(c => !!c.responsiblePlhProfessionalId)
      .map(async c => {
        const professional = await platform.adminService.getClientResponsiblePlh(context, String(c.id)).catch(() => null);
        if (professional) this.plhNames.set(String(c.id), professional.name);
      }));
  }

  private plhNameOf(client: Client): string {
    if (!client.responsiblePlhProfessionalId) return "NÃO DEFINIDO";
    return this.plhNames.get(String(client.id)) || "NÃO DEFINIDO";
  }

  /** Abre o cadastro de um Cliente com os candidatos a PLH já resolvidos. */
  private async openEditor(client: Client | null): Promise<void> {
    this.editingClient = client;
    this.plhCandidates = [];
    if (client) {
      try {
        // TODOS os Engenheiros cadastrados, venham ou não com conta,
        // assinatura ou vínculo com outro Cliente. O gate oficial de PLH
        // (`listPlhCandidatesForClient`) exige `plhClientId === clientId` e,
        // usado aqui, exigia que a pessoa JÁ fosse PLH deste Cliente para
        // poder ser escolhida como PLH dele — a lista vinha sempre vazia.
        this.plhCandidates = await platform.adminService.listEngineerProfessionals(this.requestContext(), String(client.id));
      } catch (e) {
        console.error("Failed to load PLH candidates", e);
      }
    }
    this.isModalOpen = true;
    this.render();
  }

  private requestContext(): any {
    const ctx = contextStore.get();
    return { activeClientId: ctx.activeClientId, permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
  }

  private render(): void {
    const filtered = this.clients.filter(c => {
      const matchesStatus = this.statusFilter === "ALL" || c.status === this.statusFilter;
      const term = this.searchQuery.toLowerCase();
      const matchesSearch = !term ||
        c.name.toLowerCase().includes(term) ||
        c.code.toLowerCase().includes(term) ||
        (c.legalName && c.legalName.toLowerCase().includes(term)) ||
        (c.document && c.document.includes(term));
      return matchesStatus && matchesSearch;
    });

    const canCreate = contextStore.hasPermission(PermissionCode.ClientCreate);
    const canUpdate = contextStore.hasPermission(PermissionCode.ClientUpdate);
    const canDelete = contextStore.hasPermission(PermissionCode.ClientDelete);

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Gestão de Clientes / Unidades</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Gerenciamento de clientes industriais e segregação multitenant.</p>
          </div>
          ${canCreate ? `
          <button id="btn-new-client" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
            <span>+ Novo Cliente</span>
          </button>
          ` : ''}
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center;">
          <input type="text" id="input-search" placeholder="Buscar por código, nome, razão social ou CNPJ..." value="${this.searchQuery}" style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
          <select id="select-status" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <option value="ALL" ${this.statusFilter === "ALL" ? "selected" : ""}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === "ACTIVE" ? "selected" : ""}>Ativos</option>
            <option value="INACTIVE" ${this.statusFilter === "INACTIVE" ? "selected" : ""}>Inativos</option>
          </select>
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CÓDIGO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">NOME FANTASIA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">RAZÃO SOCIAL</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CNPJ DA EMPRESA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PLH RESPONSÁVEL</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                ${(canUpdate || canDelete) ? `<th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>` : ''}
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="7" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhum cliente encontrado.</td>
                </tr>
              ` : filtered.map(c => `
                <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                  <td style="padding: 12px 16px; font-family: monospace; font-weight: 600; color: var(--color-ink);">${c.code}</td>
                  <td style="padding: 12px 16px; font-weight: 500; color: var(--ia-primary);">${c.name}</td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary);">${c.legalName || "-"}</td>
                  <td style="padding: 12px 16px; font-family: monospace; color: var(--color-text-muted); font-weight: 500;">${c.document ? formatCnpj(c.document) : "-"}</td>
                  <td style="padding: 12px 16px; color: ${c.responsiblePlhProfessionalId ? 'var(--color-text-secondary)' : 'var(--status-warning-text)'};">${this.plhNameOf(c)}</td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${c.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${c.status === 'ACTIVE' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                      ${c.status === 'ACTIVE' ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  ${(canUpdate || canDelete) ? `
                  <td style="padding: 12px 16px; text-align: right;">
                    ${canUpdate ? `<button class="btn-edit" data-id="${c.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>` : ''}
                    ${(c.status === 'ACTIVE' && canUpdate) ? `
                      <button class="btn-inactivate" data-id="${c.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Inativar</button>
                    ` : ''}
                    ${canDelete ? `<button class="btn-delete" data-id="${c.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-solid); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Excluir</button>` : ''}
                  </td>
                  ` : ''}
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>

        <style>
          .plh-swap-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.82); display:flex; align-items:center; justify-content:center; z-index:10003; padding:20px; }
          .plh-swap { background: var(--ia-bg-panel, #161A22); border: 1px solid var(--ia-line, #262C38); border-radius:12px; width:100%; max-width:560px; color: var(--ia-fg, #E6E9EF); font-size:0.85rem; overflow:hidden; }
          .plh-swap-head { padding:12px 18px; border-bottom:1px solid #262C38; font-weight:700; }
          .plh-swap-body { padding:16px 18px; display:flex; flex-direction:column; gap:12px; }
          .plh-swap-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
          .plh-swap-grid span { display:block; font-size:0.7rem; color: var(--ia-fg-muted, #8A94A6); }
          .plh-swap-grid strong { font-size:0.9rem; }
          .plh-swap-count { margin:0; padding:8px 12px; border-radius:6px; background:rgba(96,165,250,0.12); border:1px solid rgba(96,165,250,0.35); color: var(--ia-info-fg, #93c5fd); }
          .plh-swap-choice { border:1px solid #2B3340; border-radius:8px; padding:10px 14px; display:flex; flex-direction:column; gap:8px; }
          .plh-swap-choice legend { font-size:0.7rem; color: var(--ia-fg-muted, #8A94A6); padding:0 6px; }
          .plh-swap-choice label { display:flex; align-items:center; gap:8px; cursor:pointer; }
          .plh-swap-note { margin:0; font-size:0.75rem; color: var(--ia-fg-muted, #8A94A6); line-height:1.5; }
          .plh-swap-error { padding:10px 14px; border-radius:6px; background:rgba(239,68,68,0.12); border:1px solid rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); }
          .plh-swap-actions { display:flex; justify-content:flex-end; gap:8px; }
          .plh-swap-notice { margin:12px 0; padding:10px 14px; border-radius:6px; background:rgba(16,185,129,0.12); border:1px solid rgba(16,185,129,0.35); color: var(--ia-success-fg, #a7f3d0); font-size:0.82rem; }
        </style>
        ${this.replacementNotice ? `<div class="plh-swap-notice">${this.replacementNotice}</div>` : ""}
        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.plhReplacement ? this.renderPlhReplacementModal() : ""}
        ${this.deleteModal.isOpen ? this.renderDeleteModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderModal(): string {
    const isEdit = !!this.editingClient;
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 500px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Empresa / Cliente' : 'Cadastro da Empresa'}</h2>
            <button id="btn-close-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>
          <div id="modal-error-banner" style="display: none; margin-bottom: 16px; background: var(--status-danger-bg); border: 1px solid var(--status-danger-bg); color: var(--status-danger-text); padding: 10px 14px; border-radius: 6px; font-size: 0.8125rem; line-height: 1.4;"></div>
          <form id="form-client" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Código *</label>
              <input type="text" id="modal-code" value="${this.editingClient?.code || ''}" required ${isEdit ? 'readonly' : ''} placeholder="Ex: CLI-001" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: ${isEdit ? 'var(--color-bg-primary)' : 'white'};">
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Nome Fantasia *</label>
              <input type="text" id="modal-name" value="${this.editingClient?.name || ''}" required placeholder="Ex: Indústrias Beta" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Razão Social</label>
              <input type="text" id="modal-legal-name" value="${this.editingClient?.legalName || ''}" placeholder="Ex: Indústrias Beta S.A." style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                <label for="modal-document" style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted);">CNPJ da Empresa *</label>
                <span id="cnpj-status-indicator" style="font-size: 0.75rem; font-weight: 600;"></span>
              </div>
              <input type="text" id="modal-document" value="${this.editingClient?.document ? formatCnpj(this.editingClient.document) : ''}" required placeholder="00.000.000/0000-00" maxlength="18" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; font-family: monospace;">
              <span style="display: block; font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">Obrigatório: informe um CNPJ válido com 14 dígitos e verificação matemática de dígitos verificadores.</span>
            </div>
            ${isEdit ? this.renderResponsiblePlhField() : `
            <div style="background: var(--color-surface-secondary); border: 1px dashed var(--color-graphite-300); border-radius: 6px; padding: 10px 14px; font-size: 0.75rem; color: var(--color-text-secondary);">
              O <strong>PLH RESPONSÁVEL</strong> deste Cliente é definido depois de salvar, aqui mesmo no cadastro. Ele passa a responder tecnicamente por todos os Projetos e documentos do Cliente.
            </div>`}
            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  /**
   * PLH RESPONSÁVEL do Cliente — fonte oficial única da responsabilidade
   * técnica: todos os Projetos e documentos deste Cliente resolvem este
   * Profissional.
   *
   * Lista TODOS os Engenheiros do cadastro. O que ainda falta para alguém
   * poder aprovar (assinatura, habilitação neste Cliente) é mostrado ao lado
   * do nome, em vez de esconder a pessoa da lista: a escolha do responsável
   * é uma coisa, o gate da aprovação é outra.
   */
  /** Abre a confirmação da troca, já com a contagem real de pendentes. */
  private async openPlhReplacement(
    context: any,
    newPlhProfessionalId: string,
    pendingSave: { name: string; legalName: string; document: string }
  ): Promise<void> {
    const clientId = String(this.editingClient!.id);
    try {
      const preview = await platform.adminService.previewClientPlhReplacement(context, clientId, newPlhProfessionalId);
      this.plhReplacement = {
        newPlhProfessionalId,
        currentPlhName: preview.currentPlh?.name || "—",
        nextPlhName: preview.nextPlh?.name || "—",
        pendingCount: preview.pendingCount,
        // Nada é marcado por padrão: transferir pendências é decisão, não
        // consequência automática de trocar o responsável.
        reassignPending: false,
        busy: false,
        error: "",
        pendingSave
      };
    } catch (e: any) {
      this.plhReplacement = {
        newPlhProfessionalId, currentPlhName: "—", nextPlhName: "—", pendingCount: 0,
        reassignPending: false, busy: false,
        error: e?.safeMessage || e?.message || "Não foi possível preparar a substituição.",
        pendingSave
      };
    }
    this.render();
  }

  /** Confirma: grava os dados do Cliente, troca o PLH e trata os pendentes. */
  private async confirmPlhReplacement(): Promise<void> {
    const replacement = this.plhReplacement;
    if (!replacement || replacement.busy || !this.editingClient) return;
    replacement.busy = true;
    replacement.error = "";
    this.render();

    const ctx = contextStore.get();
    const context = { activeClientId: ctx.activeClientId, permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
    const clientId = String(this.editingClient.id);
    try {
      // Os demais campos do formulário são gravados primeiro, sem mexer no
      // PLH; a troca vem depois, pelo caminho que audita e reatribui.
      if (replacement.pendingSave) {
        await platform.adminService.updateClient(context, {
          ...this.editingClient,
          ...replacement.pendingSave,
          responsiblePlhProfessionalId: this.editingClient.responsiblePlhProfessionalId ?? null
        });
      }
      const result = await platform.adminService.replaceClientPlh(context, clientId, replacement.newPlhProfessionalId, {
        reassignPending: replacement.reassignPending
      });

      this.plhReplacement = null;
      this.isModalOpen = false;
      if (this.returnTo) {
        const target = this.returnTo;
        this.returnTo = null;
        this.focusPlh = false;
        router.navigate(target);
        return;
      }
      await this.loadData();
      this.replacementNotice = result.reassignedCount > 0
        ? `PLH substituído. ${result.reassignedCount} documento(s) pendente(s) transferido(s) para ${replacement.nextPlhName}.`
        : `PLH substituído. Os documentos pendentes continuam com ${replacement.currentPlhName}; os novos irão para ${replacement.nextPlhName}.`;
      this.render();
    } catch (e: any) {
      replacement.error = e?.safeMessage || e?.message || "Não foi possível substituir o PLH.";
      replacement.busy = false;
      this.render();
    }
  }

  /** Confirmação da troca — diz o que acontece com o passado e com o futuro. */
  private renderPlhReplacementModal(): string {
    const r = this.plhReplacement;
    if (!r) return "";
    const esc = (v: unknown) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
    return `
      <div class="plh-swap-backdrop">
        <div class="plh-swap">
          <div class="plh-swap-head">SUBSTITUIR PLH RESPONSÁVEL?</div>
          <div class="plh-swap-body">
            <div class="plh-swap-grid">
              <div><span>PLH atual</span><strong>${esc(r.currentPlhName)}</strong></div>
              <div><span>Novo PLH</span><strong>${esc(r.nextPlhName)}</strong></div>
            </div>
            <p>Esta alteração afeta automaticamente <strong>todos os novos documentos</strong> deste Cliente que necessitem de aprovação do PLH.</p>
            <p class="plh-swap-count">Documentos pendentes atribuídos ao PLH atual: <strong>${r.pendingCount}</strong></p>
            ${r.pendingCount > 0 ? `
              <fieldset class="plh-swap-choice">
                <legend>Como tratar os documentos pendentes existentes</legend>
                <label><input type="radio" name="plh-swap-pending" value="KEEP" ${r.reassignPending ? "" : "checked"}> Manter os documentos pendentes com o PLH atual</label>
                <label><input type="radio" name="plh-swap-pending" value="REASSIGN" ${r.reassignPending ? "checked" : ""}> Reatribuir todos os documentos pendentes ao novo PLH</label>
              </fieldset>
              <p class="plh-swap-note">Documentos já aprovados, finalizados ou assinados <strong>não</strong> são alterados: a autoria da aprovação é de quem a fez.</p>
            ` : `<p class="plh-swap-note">Nenhum documento pendente — a troca afeta apenas os próximos.</p>`}
            ${r.error ? `<div class="plh-swap-error">${esc(r.error)}</div>` : ""}
            <div class="plh-swap-actions">
              <button type="button" class="btn btn-secondary btn-sm" id="plh-swap-cancel" ${r.busy ? "disabled" : ""}>CANCELAR</button>
              <button type="button" class="btn btn-primary btn-sm" id="plh-swap-confirm" ${r.busy ? "disabled" : ""}>${r.busy ? "SUBSTITUINDO…" : "CONFIRMAR SUBSTITUIÇÃO"}</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  private renderResponsiblePlhField(): string {
    const current = this.editingClient?.responsiblePlhProfessionalId || "";
    const selected = this.plhCandidates.find(p => String(p.id) === String(current)) || null;
    const highlight = this.focusPlh ? "box-shadow: 0 0 0 3px rgba(59,130,246,0.35);" : "";
    // NOME — CONSELHO / REGISTRO: o registro é o que diferencia homônimos.
    const label = (p: Professional) => `${p.name}${p.crea ? ` — CREA ${p.crea}` : p.cft ? ` — CFT ${p.cft}` : ""}`;
    const inactive = (p: Professional) => p.status !== ProfessionalStatus.Active;
    const optionLabel = (p: Professional & { missingForApproval: string[] }) =>
      `${label(p)}${inactive(p) ? " — INATIVO" : ""}`;
    return `
      <div id="plh-responsible-block" style="border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 12px 14px; ${highlight}">
        <label for="modal-plh" style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">PLH RESPONSÁVEL</label>
        <select id="modal-plh" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
          <option value="">NÃO DEFINIDO</option>
          ${this.plhCandidates.map(p => `<option value="${p.id}" ${String(p.id) === String(current) ? "selected" : ""} ${inactive(p) && String(p.id) !== String(current) ? "disabled" : ""}>${optionLabel(p)}</option>`).join("")}
        </select>
        <span style="display: block; font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 6px;">
          ${selected
            ? `Responsável técnico atual: <strong>${label(selected)}</strong>. Vale para todos os Projetos e documentos deste Cliente.`
            : current
              ? `O Profissional vinculado deixou de estar habilitado como PLH deste Cliente. Selecione outro responsável.`
              : `<strong>NÃO DEFINIDO.</strong> Documentos que exigem responsabilidade PLH não poderão ser aprovados, assinados ou finalizados até que um responsável seja definido.`}
        </span>
        ${this.plhCandidates.length === 0 ? `<span style="display: block; font-size: 0.75rem; color: var(--status-warning-text); margin-top: 6px;">Nenhum Profissional cadastrado como Engenheiro. Cadastre-o em Administração → Pessoas e Acessos para que apareça aqui.</span>` : ""}
        ${selected && selected.missingForApproval.length > 0 ? `<span style="display: block; font-size: 0.75rem; color: var(--status-warning-text); margin-top: 6px;">O vínculo é válido, mas para <strong>aprovar e assinar</strong> ainda falta: ${selected.missingForApproval.join("; ")}.</span>` : ""}
      </div>
    `;
  }

  private attachEvents(): void {
    this.querySelector("#plh-swap-cancel")?.addEventListener("click", () => {
      // Cancelar não grava nada: o formulário continua aberto como estava.
      this.plhReplacement = null;
      this.render();
    });
    this.querySelector("#plh-swap-confirm")?.addEventListener("click", () => void this.confirmPlhReplacement());
    this.querySelectorAll('input[name="plh-swap-pending"]').forEach(input => {
      input.addEventListener("change", e => {
        if (this.plhReplacement) this.plhReplacement.reassignPending = (e.target as HTMLInputElement).value === "REASSIGN";
      });
    });

    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#select-status")?.addEventListener("change", (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-new-client")?.addEventListener("click", () => {
      this.openEditor(null);
    });

    this.querySelectorAll(".btn-edit").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.openEditor(this.clients.find(c => c.id === id) || null);
      });
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Inativar Cliente",
          message: "Deseja realmente inativar este cliente?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Excluir Cliente",
          message: "Atenção: A exclusão só será permitida se o cliente não possuir vínculos com Profissionais e OS. Deseja prosseguir?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    const docInput = this.querySelector("#modal-document") as HTMLInputElement;
    const statusIndicator = this.querySelector("#cnpj-status-indicator") as HTMLElement;
    if (docInput) {
      const updateStatus = () => {
        const rawVal = docInput.value.trim();
        if (!rawVal) {
          if (statusIndicator) statusIndicator.innerHTML = "";
          docInput.style.borderColor = "var(--color-graphite-300)";
          return;
        }
        const valid = isValidCnpj(rawVal);
        if (valid) {
          if (statusIndicator) {
            statusIndicator.innerHTML = `<span style="color: var(--status-success-text); font-weight: 600;">✓ CNPJ Válido</span>`;
          }
          docInput.style.borderColor = "var(--status-success-solid)";
        } else {
          const digits = rawVal.replace(/\D/g, "");
          if (digits.length >= 14) {
            if (statusIndicator) {
              statusIndicator.innerHTML = `<span style="color: var(--status-danger-solid); font-weight: 600;">✕ CNPJ Inválido</span>`;
            }
            docInput.style.borderColor = "var(--status-danger-solid)";
          } else {
            if (statusIndicator) {
              statusIndicator.innerHTML = `<span style="color: var(--status-warning-text); font-weight: 500;">${digits.length}/14 dígitos</span>`;
            }
            docInput.style.borderColor = "var(--color-graphite-300)";
          }
        }
      };

      docInput.addEventListener("input", (e) => {
        const input = e.target as HTMLInputElement;
        input.value = maskCnpj(input.value);
        updateStatus();
      });

      // Initial check on modal load
      updateStatus();
    }

    this.querySelector("#form-client")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const code = (this.querySelector("#modal-code") as HTMLInputElement).value;
      const name = (this.querySelector("#modal-name") as HTMLInputElement).value;
      const legalName = (this.querySelector("#modal-legal-name") as HTMLInputElement).value;
      const document = (this.querySelector("#modal-document") as HTMLInputElement).value;

      const errorBanner = this.querySelector("#modal-error-banner") as HTMLElement;
      if (errorBanner) errorBanner.style.display = "none";

      if (!isValidCnpj(document)) {
        if (errorBanner) {
          errorBanner.textContent = "CNPJ inválido. Por favor, forneça um CNPJ válido com 14 dígitos e dígitos verificadores corretos.";
          errorBanner.style.display = "block";
        }
        return;
      }

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (this.editingClient) {
          const plhSelect = this.querySelector("#modal-plh") as HTMLSelectElement | null;
          const responsiblePlhProfessionalId = plhSelect ? (plhSelect.value || null) : (this.editingClient.responsiblePlhProfessionalId ?? null);
          const previousPlhId = this.editingClient.responsiblePlhProfessionalId ?? null;

          // SUBSTITUIÇÃO (havia um PLH e passa a haver outro): confirma antes,
          // mostrando quantos documentos pendentes estão em jogo. Definir o
          // primeiro PLH, ou limpá-lo, segue pelo caminho normal.
          if (previousPlhId && responsiblePlhProfessionalId && responsiblePlhProfessionalId !== previousPlhId) {
            await this.openPlhReplacement(dummyContext, responsiblePlhProfessionalId, { name, legalName, document });
            return;
          }

          await platform.adminService.updateClient(dummyContext, {
            ...this.editingClient,
            name,
            legalName,
            document,
            responsiblePlhProfessionalId
          });
        } else {
          await platform.adminService.createClient(dummyContext, {
            code,
            name,
            legalName,
            document
          });
        }
        this.isModalOpen = false;
        // Deep link a partir de um documento bloqueado: volta ao documento.
        // O usuário confirma o fechamento de novo — nada é finalizado sozinho.
        if (this.returnTo) {
          const target = this.returnTo;
          this.returnTo = null;
          this.focusPlh = false;
          router.navigate(target);
          return;
        }
        await this.loadData();
      } catch (err: any) {
        if (errorBanner) {
          errorBanner.textContent = err.message || "Erro ao salvar empresa.";
          errorBanner.style.display = "block";
        } else {
          console.error(err);
        }
      }
    });

    this.querySelector("#btn-confirm-delete")?.addEventListener("click", async () => {
      const { id, title } = this.deleteModal;
      const isInactivate = title.includes("Inativar");
      
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId || "",
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (isInactivate) {
          await platform.adminService.inactivateClient(dummyContext, id);
        } else {
          await platform.adminService.deleteClient(dummyContext, id);
        }
        this.deleteModal.isOpen = false;
        await this.loadData();
      } catch (e: any) {
        this.deleteModal.error = e.message || "Erro ao executar ação.";
        this.render();
      }
    });

    this.querySelector("#btn-cancel-delete")?.addEventListener("click", () => {
      this.deleteModal.isOpen = false;
      this.render();
    });
  }

  private renderDeleteModal(): string {
    const { title, message, error } = this.deleteModal;
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 450px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); text-align: center;">
          <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 16px 0;">${title}</h2>
          <p style="color: var(--color-text-secondary); font-size: 0.95rem; margin-bottom: 24px;">${message}</p>
          ${error ? `<div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px; text-align: left;">${error}</div>` : ''}
          <div style="display: flex; justify-content: center; gap: 12px;">
            <button id="btn-cancel-delete" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
            <button id="btn-confirm-delete" style="background: var(--status-danger-solid); border: none; border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">Confirmar</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-admin-clients-page")) {
  customElements.define("app-admin-clients-page", AdminClientsPage);
}



